import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  OdooArchivo, OdooBitacoraFiltro, OdooBitacoraItem, OdooCarga, OdooConfig, OdooConfigCambio,
  OdooMaestro, OdooMaestroDetalle, OdooMaestrosFiltro, OdooPagina, OdooPrueba, OdooRespuesta,
  OdooResumen, OdooTipoMaestro,
} from './odoo.models';

/** Texto sencillo para el usuario a partir de un fallo de red. */
export function mensajeDeFallo(e: unknown): string {
  if (e instanceof HttpErrorResponse) {
    const cuerpo: unknown = e.error;
    if (cuerpo && typeof cuerpo === 'object') {
      const msg = (cuerpo as { msg?: unknown; message?: unknown }).msg ?? (cuerpo as { message?: unknown }).message;
      if (typeof msg === 'string' && msg) return msg;
    }
    if (e.status === 0) return 'No hay conexión con el servidor. Revisa tu internet y vuelve a intentar.';
    if (e.status === 401) return 'Tu sesión venció. Entra de nuevo.';
    if (e.status === 403) return 'No tienes permiso para esto.';
    if (e.status === 404) return 'No se encontró lo que buscabas.';
  }
  return 'Algo salió mal. Vuelve a intentar en unos segundos.';
}

/**
 * Convierte "parámetros → petición" en un flujo de estados para el async pipe.
 * switchMap cancela la petición anterior; conserva los datos previos mientras carga.
 */
export function conCarga<P, T>(params$: Observable<P>, pedir: (p: P) => Observable<T>): Observable<OdooCarga<T>> {
  let previo: T | null = null;
  return params$.pipe(
    switchMap((p) =>
      pedir(p).pipe(
        map((datos): OdooCarga<T> => { previo = datos; return { cargando: false, error: null, datos }; }),
        catchError((e: unknown) => of<OdooCarga<T>>({ cargando: false, error: mensajeDeFallo(e), datos: null })),
        startWith<OdooCarga<T>>({ cargando: true, error: null, datos: previo }),
      ),
    ),
  );
}

@Injectable({ providedIn: 'root' })
export class OdooService {
  private readonly base = `${environment.BASE_LA}/api/odoo`;

  /** Último resumen visto: se pinta al instante al volver a la pestaña y se refresca en silencio. */
  resumenGuardado: OdooResumen | null = null;

  constructor(private http: HttpClient) {}

  private armar(obj: Record<string, string | number | boolean | null | undefined>): HttpParams {
    let p = new HttpParams();
    Object.keys(obj).forEach((k) => {
      const v = obj[k];
      if (v !== '' && v !== null && v !== undefined) p = p.set(k, String(v));
    });
    return p;
  }

  resumen(): Observable<OdooResumen> {
    return this.http.get<OdooRespuesta<OdooResumen>>(`${this.base}/resumen`).pipe(
      map((r) => { this.resumenGuardado = r.data; return r.data; }),
    );
  }

  maestros(f: OdooMaestrosFiltro): Observable<OdooPagina<OdooMaestro>> {
    const params = this.armar({ tipo: f.tipo, estado: f.estado, q: f.q.trim(), page: f.page, limit: f.limit });
    return this.http.get<OdooRespuesta<OdooPagina<OdooMaestro>>>(`${this.base}/maestros`, { params }).pipe(map((r) => r.data));
  }

  maestro(tipo: OdooTipoMaestro, sourceId: string): Observable<OdooMaestroDetalle> {
    return this.http
      .get<OdooRespuesta<OdooMaestroDetalle>>(`${this.base}/maestros/${tipo}/${encodeURIComponent(sourceId)}`)
      .pipe(map((r) => r.data));
  }

  reenviar(tipo: OdooTipoMaestro, sourceId: string): Observable<OdooMaestro> {
    return this.http
      .post<OdooRespuesta<OdooMaestro>>(`${this.base}/maestros/${tipo}/${encodeURIComponent(sourceId)}/enviar`, {})
      .pipe(map((r) => r.data));
  }

  archivos(page: number, limit: number): Observable<OdooPagina<OdooArchivo>> {
    const params = this.armar({ page, limit });
    return this.http.get<OdooRespuesta<OdooPagina<OdooArchivo>>>(`${this.base}/archivos`, { params }).pipe(map((r) => r.data));
  }

  generarArchivos(fecha?: string): Observable<OdooArchivo[]> {
    const body: { fecha?: string } = fecha ? { fecha } : {};
    return this.http.post<OdooRespuesta<OdooArchivo[]>>(`${this.base}/archivos/generar`, body).pipe(map((r) => r.data));
  }

  descargar(id: string): Observable<Blob> {
    return this.http.get(`${this.base}/archivos/${encodeURIComponent(id)}/descargar`, { responseType: 'blob' });
  }

  bitacora(f: OdooBitacoraFiltro): Observable<OdooPagina<OdooBitacoraItem>> {
    const params = this.armar({ tipo: f.tipo, ok: f.ok, page: f.page, limit: f.limit });
    return this.http.get<OdooRespuesta<OdooPagina<OdooBitacoraItem>>>(`${this.base}/bitacora`, { params }).pipe(map((r) => r.data));
  }

  config(): Observable<OdooConfig> {
    return this.http.get<OdooRespuesta<OdooConfig>>(`${this.base}/config`).pipe(map((r) => r.data));
  }

  guardarConfig(cambio: OdooConfigCambio): Observable<OdooConfig> {
    return this.http.patch<OdooRespuesta<OdooConfig>>(`${this.base}/config`, cambio).pipe(map((r) => r.data));
  }

  probarConexion(): Observable<OdooPrueba> {
    return this.http.post<OdooRespuesta<OdooPrueba>>(`${this.base}/config/probar`, {}).pipe(map((r) => r.data));
  }
}
